<template>
  <div
    :class="[
      'creditcard-card border rounded-xl p-4 transition-shadow cursor-pointer',
      card.active === false
        ? 'border-gray-300 bg-gray-100'
        : 'border-gray-200 bg-white hover:shadow-md'
    ]"
    @click="$emit('click', card)"
  >
    <!-- Header with name and status badge -->
    <div class="flex items-start justify-between mb-3">
      <div class="flex-1 min-w-0">
        <h3 class="text-base font-semibold text-gray-900 mb-1 truncate">
          {{ card.name }}
        </h3>
        <p
          v-if="card.description && showDescription"
          class="text-sm text-gray-500 line-clamp-2"
        >
          {{ truncatedDescription }}
        </p>
      </div>
      <div class="flex gap-1 flex-shrink-0 ml-2">
        <span
          v-if="card.is_primary"
          class="rounded-md px-2 py-1 text-xs font-medium whitespace-nowrap text-blue-700 bg-blue-50 border border-blue-500"
        >
          Primair
        </span>
        <span
          :class="[
            statusClasses,
            'rounded-md px-2 py-1 text-xs font-medium whitespace-nowrap'
          ]"
        >
          {{ statusText }}
        </span>
      </div>
    </div>

    <!-- Credits visualization -->
    <div class="mb-3">
      <template v-if="showCredits">
        <div v-if="isUnlimited" class="text-sm text-gray-600">
          <span class="font-medium">Onbeperkt gebruik</span>
          <span v-if="seasonEndDate">t/m {{ seasonEndDate }}</span>
        </div>
        <div v-else class="credits-bar">
          <div class="flex justify-between text-sm mb-1">
            <span class="text-gray-600">Strippen</span>
            <span class="font-medium">{{ remaining }} / {{ total }}</span>
          </div>
          <div class="w-full bg-gray-200 rounded-full h-2">
            <div
              class="h-2 rounded-full transition-all"
              :class="creditsBarColor"
              :style="{ width: `${creditsPercentage}%` }"
            ></div>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="text-lg font-semibold text-gray-900">
          {{ formattedCost }}
        </div>
        <div class="text-sm text-gray-500">
          {{ isUnlimited ? 'Onbeperkt gebruik' : `${total}x te gebruiken` }}
        </div>
      </template>
    </div>

    <!-- Footer with date and action -->
    <div
      class="flex items-center justify-between pt-3 border-t border-gray-100"
    >
      <div v-if="purchasedAt" class="text-xs text-gray-500">
        Gekocht op {{ purchasedAt }}
      </div>
      <div v-else class="text-xs text-gray-500">
        {{ validityInfo }}
      </div>
      <slot name="action">
        <button
          v-if="showBuyButton"
          class="button button-primary button-xs"
          @click.stop="$emit('buy', card)"
        >
          Koop nu
        </button>
      </slot>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CreditcardCard',
  props: {
    card: {
      type: Object,
      required: true
    },
    showCredits: {
      type: Boolean,
      default: false
    },
    showBuyButton: {
      type: Boolean,
      default: false
    },
    showDescription: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    isSeasonCard() {
      return this.card?.type === 'season_card'
    },
    isUnlimited() {
      return (
        this.isSeasonCard ||
        this.card?.cp_details?.remaining === null ||
        this.card?.details?.times_to_use === null
      )
    },
    remaining() {
      return this.card?.cp_details?.remaining || 0
    },
    total() {
      return this.card?.details?.times_to_use || 0
    },
    creditsPercentage() {
      if (this.isUnlimited || this.total === 0) return 100
      return Math.round((this.remaining / this.total) * 100)
    },
    creditsBarColor() {
      if (this.creditsPercentage > 50) return 'bg-green-600'
      if (this.creditsPercentage > 25) return 'bg-yellow-500'
      return 'bg-red-500'
    },
    seasonNotStarted() {
      if (!this.isSeasonCard) return false
      const seasonStart =
        this.card?.cp_details?.season_start || this.card?.details?.season_start
      return seasonStart && new Date(seasonStart) > new Date()
    },
    cardIsExpired() {
      if (this.card.isOwner) return false
      if (this.isSeasonCard) {
        const seasonEnd =
          this.card?.cp_details?.season_end || this.card?.details?.season_end
        return seasonEnd && new Date(seasonEnd) < new Date()
      }
      const validUntil =
        this.card?.cp_details?.valid_until || this.card?.valid_until
      return (
        (validUntil && new Date(validUntil) < new Date()) ||
        this.card?.cp_details?.status === 'expired' ||
        this.card?.status === 'expired'
      )
    },
    statusText() {
      if (this.card.isOwner) return 'Eigenaar'
      if (!this.card.active) return 'Inactief'
      if (this.seasonNotStarted) return 'Nog niet actief'
      if (this.cardIsExpired) return 'Verlopen'
      if (!this.isUnlimited && this.remaining === 0) return 'Op'
      return 'Actief'
    },
    statusClasses() {
      const statusMap = {
        Eigenaar: 'text-purple-700 bg-purple-50 border border-purple-500',
        Actief: 'text-green-700 bg-green-50 border border-green-500',
        Verlopen: 'text-yellow-800 bg-yellow-50 border border-yellow-500',
        Op: 'text-red-700 bg-red-50 border border-red-500',
        'Nog niet actief': 'text-blue-700 bg-blue-50 border border-blue-500',
        Inactief: 'text-gray-700 bg-gray-50 border border-gray-500'
      }
      return statusMap[this.statusText] || statusMap['Inactief']
    },
    seasonEndDate() {
      const endDate =
        this.card?.cp_details?.season_end || this.card?.details?.season_end
      return endDate ? new Date(endDate).toLocaleDateString() : ''
    },
    purchasedAt() {
      return (
        this.card?.purchased_at || this.card?.cp_details?.purchased_at || null
      )
    },
    validityInfo() {
      if (this.isSeasonCard && this.card?.details?.season_start) {
        const startDate = new Date(
          this.card.details.season_start
        ).toLocaleDateString()
        return `Seizoen: ${startDate} - ${this.seasonEndDate}`
      }
      const period = this.card?.details?.validity_period
      if (period === 'indefinitely') return 'Onbeperkt geldig na aankoop'
      if (period === '1,year') return '1 jaar geldig na aankoop'
      if (period === '6,month') return 'Een half jaar geldig na aankoop'
      return ''
    },
    truncatedDescription() {
      if (this.card.description && this.card.description.length > 80) {
        return this.card.description.slice(0, 80) + '...'
      }
      return this.card.description
    },
    formattedCost() {
      if (this.card.cost === undefined || this.card.cost === null) return ''
      return new Intl.NumberFormat('nl-NL', {
        style: 'currency',
        currency: 'EUR'
      }).format(this.card.cost)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/variables.scss';

.creditcard-card {
  &:hover {
    border-color: $ipGreen;
  }
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
